import { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';

import { useRefreshOffers } from 'hooks/loyalty/use-refresh-offers';

import type { CartState } from '../types';
import { hasOffers } from '../utils';

import { useCartLiveValidation } from './use-cart-live-validation';

type UseCartInitParams = {
  cartEntries: ICartEntry[];
  setCartState: Dispatch<SetStateAction<CartState>>;
};

// Minimal cart reprice feedback is shown for at least 350ms
// This is to avoid flickering when the minimal cart is repriced quickly
const STORE_REPRICE_FEEDBACK_MS = 350;

export const useCartInit = ({ cartEntries, setCartState }: UseCartInitParams) => {
  useRefreshOffers({ skip: !hasOffers(cartEntries) });
  const { validateCartNow, requestCartValidation } = useCartLiveValidation({ cartEntries });
  const {
    isMinimalCartStoreRepricing,
    shouldRepriceMinimalCartForStore,
    repriceMinimalCartEntries,
  } = useMinimalCart();
  const repricingStartedAtRef = useRef<number | null>(null);
  const hideLoaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCartState(prev => {
      if (
        prev.validateCartNow === validateCartNow &&
        prev.requestCartValidation === requestCartValidation
      ) {
        return prev;
      }
      return {
        ...prev,
        validateCartNow,
        requestCartValidation,
      };
    });
  }, [requestCartValidation, setCartState, validateCartNow]);

  useEffect(() => {
    if (!shouldRepriceMinimalCartForStore) {
      return;
    }

    repriceMinimalCartEntries();
  }, [repriceMinimalCartEntries, shouldRepriceMinimalCartForStore]);

  useEffect(() => {
    if (hideLoaderTimeoutRef.current) {
      clearTimeout(hideLoaderTimeoutRef.current);
      hideLoaderTimeoutRef.current = null;
    }

    if (isMinimalCartStoreRepricing) {
      repricingStartedAtRef.current = Date.now();
      setCartState(prev =>
        prev.isStoreRepricingVisible ? prev : { ...prev, isStoreRepricingVisible: true }
      );
      return;
    }

    setCartState(prev => {
      if (!prev.isStoreRepricingVisible) {
        return prev;
      }

      const elapsedMs = repricingStartedAtRef.current
        ? Date.now() - repricingStartedAtRef.current
        : 0;
      const remainingMs = Math.max(STORE_REPRICE_FEEDBACK_MS - elapsedMs, 0);

      if (remainingMs === 0) {
        return {
          ...prev,
          isStoreRepricingVisible: false,
        };
      }

      hideLoaderTimeoutRef.current = setTimeout(() => {
        setCartState(current =>
          current.isStoreRepricingVisible ? { ...current, isStoreRepricingVisible: false } : current
        );
      }, remainingMs);

      return prev;
    });
  }, [isMinimalCartStoreRepricing, setCartState]);

  useEffect(() => {
    return () => {
      if (hideLoaderTimeoutRef.current) {
        clearTimeout(hideLoaderTimeoutRef.current);
      }
    };
  }, []);
};
